/* eslint-disable max-lines, max-depth, max-depth, max-statements, complexity */
// about vuex
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapActions, mapState } from "vuex";
import { markRaw } from "vue";
import {
  executePolling,
  postResponseCheckDefaultAction,
  POLLING_ERROR_GET_INVALID_RESPONSE,
  REQUEST_STATUS_COMPLETED,
  REQUEST_STATUS_IN_PROGRESS,
  REQUEST_STATUS_FAILED,
  POLLING_ERROR_POST_INVALID_RESPONSE
} from "../../../../../share/util/api/polling";
import { executeSyncRequest } from "../../../../../share/util/api/syncRequest";
import { OBD_DM } from '../../../../../constants/usageCountTypes';
import { apiErrorDefaultAction } from '../../../../../share/util/api/errorAction';
import apiService from '../../../../../share/util/api/service';
import alert from "../../../../../../../share/src/alert";
import loading from "../../../../../../../share/src/components/vue/ProcessingIcon";
import WrappedTeleport from "../../../../../../../share/src/components/vue/WrappedTeleport";
import session from "../../../../../../../share/src/session";
import util from "../../../../../../../share/src/util/utils";
import diagnose from "../../../../../../../share/src/util/diagnose";
import obdDataMonitorExecute from "../../../../vue/obd/individual/dataMonitor/ObdDataMonitorMeasure";
import obdDataMonitorSelect from "../../../../vue/obd/individual/dataMonitor/ObdDataMonitorSelect";
import error from '../../../../../common/nodejs/errors';
import logWrapper from "../../../../../../../share/src/logWrapper";
import config from "../../../../../../../share/src/config";
import { WORKER, WORKER_TASK, WORKER_EVENT, DATA_STORAGE } from "../../../dataMonitor/dataMonitorUtil";
import { convertKeyForPidToDid } from '../../../../../util/obd/pid';

const { v4: uuid } = require("uuid");

const ERR_MSG_KEY = {
  INFO_GET: "get",
  INIT: "init",
  MEASURE_START: "start",
  MEASURE_STOP: "stop",
  COMMUNICATION: "communication",
  INTDASH: "intdash",
  REQUESTS_START: "requests_start",
  FETCH: "fetch",
  REQUESTS_EXECUTION: "requests_execution",
  MEASUREMENT_LIMIT: "measurement_limit",
};

// ポーリング間隔（ミリ秒）
const INTERVAL = {
  MEASURE_SLEEP: 14000, // Polling interval sleep（Completed確認後）共通関数のインターバルと合わせて15秒停止
  WATCH_TIMER: 10000, // 計測時間監視インターバル(ms)
  WAIT_TIME: 1000, // データモニタ開始のPollingのCompletedまでPollingの待ち時間
  WORKER_SLEEP: 500, // Workerの待機時間
  NETWORK_ERROR: 5000, // APIにてネットワークエラーなどの予期せぬエラー発生時のリトライインターバル
};

// API失敗時の再実行回数
const RETRY = {
  MEASURE_START: Number.MAX_VALUE, // データ総件数の上限で計測を停止するため、上限は設けない
  MEASURE_STOP: Number.MAX_VALUE,
  MEASURE_STOP_NETWORK_ERROR: 12, // 停止APIにてネットワークエラー発生時のリトライ回数
  MEASURE_POLLING_NETWORK_ERROR: 4 // ポーリング中にてネットワークエラー発生時のリトライ回数
};

// 最大連続計測時間(msec)
const MAX_TIME = 3600 * 1000;

// データ総件数の上限(2000/秒 × 3780秒。最大実行時間は1時間だが、3分ほどデータ数は多めにしておく)
const DATA_TOTAL_COUNT_LIMIT = 2000 * 3780;

// intdashサーバのプロジェクトUUID
const INTDASH_PROJECT_UUID = "00000000-0000-0000-0000-000000000000";

export default {
  name: "ObdDataMonitor",
  data() {
    return {
      processing: false,
      stopProcessing: false,
      checkProcessing: false,
      // 画面に表示する為の機能リスト
      filterFunctionList: [],
      showDataMonitorExecute: false,
      menuResponse: null,
      conversionList: {},
      // 変換情報を早く引くため、DIDごとのインデックスを持つオブジェクト
      didConversionMap: markRaw({}),
      referenceValues: [],
      didSizes: {},
      dataMonitorId: "",
      // 選択項目
      selectedFuncs: [],
      selectedFunctionHash: {},
      // グラフ設定
      graphConfigInfo: {
        xRange: 0,
        yRange: {},
        lineSetting: {}
      },
      completeResolve: () => { },

      // 以下API
      // intdashサーバ計測UUID
      measurementUuid: "",
      // Intdashの接続先
      endpoint: "",
      // APIトークン
      apiToken: "",
      // edge ID
      edgeId: "",
      // トークン有効期限
      tkExpire: "",
      // リクエストID
      requestId: "",
      // メモAPI用 uuid
      memoUuid: "",

      // 計測開始 API 取得成功 FLG
      isCompleted: false,
      // Intdashとの接続完了 FLG
      isConnected: false,
      // Polling中 判定 FLG
      isPolling: true,
      // ポーリング停止指示
      hasStopOrder: false,
      // 計測開始時間(ナノ秒単位のUNIXタイムスタンプ)。ただしミリ秒以下は0になるように調整
      baseTime: null,
      // 計測開始時間のミリ秒以下が0となるように、調整した時間
      baseTimeOffset: null,
      // エラーフラグ
      isError: false,
      // サインアウト
      signout: false,

      // Workerエラーフラグ
      workerError: false,
      // マーカー情報
      markerInfo: markRaw([]),
      // 項目ごとのデータ（単位、平均、最大最小など）
      funcValues: {},
      // Web workerのインスタンス
      workerInstance: markRaw({}),
      // 対象ECUのID。routeのStateから取得できない再生画面でのみ使用する
      specifiedSystemId: "",
      // データ再生からの呼び出しか否か（OBDでは不使用）
      isDataRecollection: false,
      // 子コンポーネントのMeasureを明示的に消す
      unmountMeasure: false,
      // HEV系AT対応：テストケースメニュータイトルキー(OBDでは不使用)
      hevAtMenuTitleKey: "",
      // RestfulWorkerで除外されたデータ
      escapedDataPoints: {},
    };
  },

  props: {
    // 項目選択画面表示フラグ
    showSelect: { type: Boolean, default: true, required: true },
    // 全画面モーダルの表示有無
    showModal: { type: Boolean, default: false, required: false },
    // 計測中フラグ
    inExecution: { type: Boolean, default: false, required: true },
    // 計測データ再生中フラグ
    inPlaying: { type: Boolean, default: false, required: true },
    // システム間移動判定
    isUseCache: undefined,
    // 親画面名称
    parentName: { type: String, default: "data_monitor", required: false },
    // 特殊機能判定
    isSpecial: { type: Boolean, default: false, required: false },
    // ActiveTestの強調表示項目リスト ※OBDでは不使用
    highlightItems: { type: Array, 'default': () => ([]), required: false },
    // DM機能の設定インスタンス
    dataMonitorFunctionSettings: { type: Object, required: true },
    // 計測画面全体をteleportさせるモーダルダイアログのID
    modalContentId: { type: String, default: undefined, required: false },
    // グラフ設定ボタンなどをteleportさせる行先のDOMのID
    graphSettingsDestinationId: { type: String, default: undefined, required: false },
    // 選択したアクティブテストの情報（OBDでは不使用）
    selectedAtFunc: { type: Object, default: {}, required: false },
    // アクティブテスト駆動状態（OBDでは不使用）
    isActiveTestDriving: { type: Boolean, default: false, required: false },
    // （データ再生）アクティブテストIO/Routine Control対象の情報（OBDでは不使用）
    recollectionAtInfo: { type: Array, 'default': () => ([]), required: false },
    // （データ再生）アクティブテスト駆動状態一覧（OBDでは不使用）
    atOperatingStatusList: { type: Array, 'default': () => ([]), required: false },
    // （データ再生）Phase6対象HEV系ATか否か（OBDでは不使用）
    isPhase6AtRecollection: { type: Boolean, 'default': false, required: false },
    // (保存データ一覧) CSVの出力のみの利用か
    isOnlyCsvExport: { type: Boolean, default: false, required: false },
  },

  components: {
    "processing-view": loading,
    "data-monitor-execute": obdDataMonitorExecute,
    "data-monitor-select": obdDataMonitorSelect,
    "wrapped-teleport": WrappedTeleport
  },

  watch: {
    showSelect: {
      handler(val) {
        if (val) {
          this.clearMeasuredData();
        }
      },
    },
    isCompleted: {
      handler(val) {
        // 計測開始API取得完了かつIntdashとの接続完了の場合、loading解除
        if (val && this.isConnected) {
          this.processing = false;
        }
      },
    },
    isConnected: {
      handler(val) {
        // 計測開始API取得完了かつIntdashとの接続完了の場合、loading解除
        if (val && this.isCompleted) {
          this.processing = false;
        }
      },
    },
  },

  computed: {
    ...mapState({
      language: (state) => state.systems.language,
      netAppId: (state) => state.netAppConnector.pairingAppId,
      userProperty: (state) => state.userProperty,
      individualMenus: (state) => state.vehicle.individualMenus,
      obdDataId: state => state.obd.obdDataId,
      cachedProjectDataSource: state => state.cache.projectDataSource,
      isTestModeState: (state) => state.netAppConnector.isTestMode,
    }),
    selectedDidList() {
      const res = this.selectedFuncs.map((func) => func.data_monitor_id);
      return res;
    },

    itemComparator() {
      if (this.sortAsc) {
        return (a, b) => a.sort_id - b.sort_id;
      } else {
        return (a, b) => b.sort_id - a.sort_id;
      }
    },
    // DMAT判定
    isDataMonitor() {
      return this.parentName === "data_monitor";
    },
    isDisplayProcessing() {
      return this.processing || this.stopProcessing
        // 計測終了後もcheckGetApiResponseをリトライしている場合
        || (!this.inExecution && this.checkProcessing);
    },

    systemNo() {
      return this.$route.params.systemNo;
    },

    // 診断系のAPIは複数画面から使用されているため
    // 現在の画面で使用されるAPIが診断/データ再生/個別診断/OBDシステムのうちどの機能なのか判別する
    operationScreen() {
      return diagnose.operationFrom(this.$router.currentRoute.value.name);
    },
    // HEV系ATのDM表示に用いる（OBDでは不使用）
    isHev() { return this.atInfo && (this.atInfo.length > 0); },
    atInfo() { return this.isDataRecollection ? this.recollectionAtInfo : this.selectedAtFunc?.at_info; },
    // データ再生画面の場合はisTestModeをfalseにする
    isTestMode() { return this.isTestModeState && !this.dataMonitorFunctionSettings.isPlay(); },
  },

  methods: {
    ...mapActions("auth", ["logout", "resetSession", "stopSessionTimer", "resetSessionTimer"]),
    ...mapActions("observation", ["usageCountUp"]),
    ...mapActions('app', ['setIsTimeoutLocked']),
    ...mapActions("app", ["setViewProcessMode"]),

    /**
     * componentのmounted時に呼び出す処理
     */
    mountedLogic() {
      // 作業サポートから呼ばれる場合、Intdashのデータは永続化しない
      if (this.isMonitoringOnly) {
        this.isPersist = false;
      }
    },

    /**
     * 子コンポーネントのDataMonitorMeasureのMount・Unmountを親コンポーネントに通知する
     * @param {boolean} isMounted Mountされたか否か
     */
    measureMountedEvent(isMounted) {
      this.$emit("measure-mounted", isMounted);
    },

    /**
     * 処理中の表示
     * @param {*} flg 表示するか否か
     */
    displayProcessing(flg) {
      this.processing = flg;
    },

    /** 計測結果を破棄 */
    clearMeasuredData() {
      this.dataMonitorList = [];
      this.baseTime = null;
      this.baseTimeOffset = null;
      this.funcValues = {};
      this.escapedDataPoints = {};
    },

    /** 計測開始（初回） */
    startExecute(selectedFunctionList = []) {
      // 初回の計測開始時に、全画面モーダルを自動表示する
      this.$emit("maximum");
      // 計測画面へ遷移
      this.$emit("toggle-show-select", false);
      // 選択項目を取得
      this.conversionList = {};
      const selectedFuncsHash = {};
      for (const selectedFunction of selectedFunctionList) {
        const { data_monitor_id, ...conv } = selectedFunction;
        selectedFuncsHash[data_monitor_id] = conv;
      }
      this.selectedFuncs = selectedFunctionList;
      this.selectedFunctionHash = selectedFuncsHash;
      if (this.isDataMonitor || selectedFunctionList.length > 0) {
        this.startMeasureApi();
      }
    },

    /** モニタ開始 */
    startMonitor() {
      this.startMeasureApi();
    },

    /**
     * モニタ停止
     * @param {boolean} errorTerminateFlg errorTerminateFlgフラグ(trueの場合typeをerrorTerminateにする)
     */
    async stopMonitor(errorTerminateFlg = false) {
      // 停止の成否に関わらず、停止実行時点でツリーは戻す
      this.setViewProcessMode(config.PROCESS_IDLING);
      try {
        this.stopProcessing = true;
        await this.measureStop(this.selectedDidList, errorTerminateFlg);
      } finally {
        this.stopProcessing = false;
      }

      this.$emit("change-measure-id", this.memoUuid);
    },

    /** データ件数の通知 */
    notifyDataTotalCount(count) {
      // 件数上限オーバーの場合。ただし再生画面ではエラーを出さない
      if (this.isPolling && count > DATA_TOTAL_COUNT_LIMIT && !this.specifiedSystemId) {
        this.isPolling = false;
        // 計測API停止
        this.stopMonitor();
        alert.showWarning(
          this,
          null,
          this.$t("individual.alert_data_monitor_data_limit_exceeded"),
          () => { }
        );
      }
    },

    /** 計測データ再生/停止状態切替 */
    togglePlaying(flg) {
      this.$emit("toggle-playing", flg);
    },

    /** 選択項目の言語変更データ受取 */
    changeSelectedFuncs(list) {
      const selectedFuncsHash = {};
      for (const selectedFunction of list) {
        const { data_monitor_id, ...conv } = selectedFunction;
        selectedFuncsHash[data_monitor_id] = conv;
      }
      this.selectedFuncs = list;
      this.selectedFunctionHash = selectedFuncsHash;
    },

    /** グラフ設定情報を保持 */
    setGraphConfig(xRange, yRange, lineSetting) {
      this.graphConfigInfo.xRange = xRange;
      // yRangeに項目が存在していれば更新、存在しなければ追加、設定済みの項目はそのまま
      Object.keys(yRange).forEach((did) => {
        this.graphConfigInfo.yRange[did] = yRange[did];
      });

      // lineSettingに項目が存在していれば更新、存在しなければ追加、設定済みの項目はそのまま
      if (lineSetting) {
        Object.keys(lineSetting).forEach((did) => {
          this.graphConfigInfo.lineSetting[did] = lineSetting[did];
        });
      }
    },

    /** グラフ項目数を設定 */
    changeCheckValue(checkLines) {
      this.$emit("change-check-value", checkLines);
    },

    /**
     * 現在タブの変更を親コンポーネントへ通知
     * @param {string} currentTab 現在のタブ
     */
    currentTabChanged(currentTab) {
      this.$emit("current-tab-changed", currentTab);
    },

    /**
     * ズーム/パン状態の変更を親コンポーネントへ通知
     * @param {boolean} isZoomPanActive
     */
    zoomPanStateChanged(isZoomPanActive) {
      this.$emit("zoom-pan-state-changed", isZoomPanActive);
    },

    /** 計測画面の範囲を取得 */
    getAreaStyle() {
      let className = this.showModal ? "dm-area-modal" : "dm-area";
      if (this.parentName === "data_recollection") {
        className = this.showModal ? "dm-area-modal" : "dm-area-rc";
      }
      return {
        [className]: true,
      };
    },

    /**
     * 子コンポーネントから計測データを取得する
     * @returns 計測データ
     */
    getMeasureData() {
      return this.$refs.data_monitor_execute.convertedData;
    },

    /**
     * タイムラインコンポーネントからマーカー情報を取得する
     * @returns マーカー情報
     */
    getMarkers() {
      return this.$refs.data_monitor_execute.getMarkers();
    },

    /**
     * 子コンポーネントから描画間隔を取得する
     * @returns {number} 描画間隔
     */
    getDrawInterval() {
      return this.$refs.data_monitor_execute.drawInterval;
    },

    /**
     * 計測開始API始動
     */
    async startMeasureApi() {
      // 計測FLGを開始に変更
      this.$emit("toggle-execute", true);
      // OBD使用回数をカウント
      this.usageCountUp({ type: OBD_DM });
      try {
        // 計測中はツリー非表示にする
        this.setViewProcessMode(config.CONTAINER_EXECUTING);
        // 処理中フラグONにし、計測開始API取得/Intdashとの接続完了 までローディングアイコンを出す
        this.processing = true;
        this.isError = false;

        const token = session.getToken();

        this.measurementUuid = uuid();
        this.isPolling = true;
        this.isCompleted = false;
        this.isConnected = false;

        const isSucceedIntdash = await this.setIntdashInfo();
        // intdashAccess情報取得失敗の場合は以降の処理は行わない。
        if (!isSucceedIntdash) {
          // ボタンの活性・非活性変更
          this.$emit("toggle-execute", false);
          return;
        }

        const isSucceedConversionList = await this.setConversionList(this.selectedDidList);
        // monitorConversion API失敗の場合は以降の処理は行わない。
        if (!isSucceedConversionList) {
          // ボタンの活性・非活性変更
          this.$emit("toggle-execute", false);
          return;
        }

        this.hasStopOrder = false;

        // iSCPダウンストリーム処理開始
        this.startDownstream();

        // データモニタ実行API
        this.measureStart(token, this.selectedDidList);

        // iSCPダウンストリームの終了待ち
        while (this.isPolling && !this.hasStopOrder) {
          await util.sleep(INTERVAL.WAIT_TIME);
        }

      } finally {
        // 処理中フラグOFF
        this.processing = false;
        // 計測終了時はツリーは戻す
        this.setViewProcessMode(config.PROCESS_IDLING);
      }
    },

    /**
     * Intdashへのアクセス情報を取得して保持する
     * @returns {boolean} 処理の成否
     */
    async setIntdashInfo() {
      const EXPIRY_MARGIN_SEC = 7200; // 2時間
      const expiredTime = new Date(this.tkExpire * 1000);
      const now = new Date();
      const isExpired = (expiredTime - now) / 1000 <= EXPIRY_MARGIN_SEC;

      // intdashAccess情報がない or tokenの有効期限が切れた場合
      if (
        this.apiToken === "" ||
        (!this.specifiedSystemId && this.edgeId === "") ||
        this.tkExpire === "" ||
        this.endpoint === "" ||
        isExpired ||
        // 有効期限変更対応前のトークンの場合、トークンを取りなおす。
        expiredTime.getFullYear() - now.getFullYear() >= 1
      ) {
        // intdashAccess API
        await this.getIntdashAccess();
        // intdashAccess情報取得を実施しても情報が無い場合はintdashAccess情報取得失敗とする
        return (
          this.apiToken !== "" &&
          (this.specifiedSystemId || this.edgeId !== "") &&
          this.tkExpire !== "" &&
          this.endpoint !== ""
        );
      }
      return true;
    },

    /**
     * 変換情報を取得して保持する
     * @param {array} didList 変換情報を取得するDIDのリスト
     * @returns {boolean} 処理の成否
     */
    async setConversionList(didList) {
      // monitorConversion API
      const monitorConversionList = await this.getMonitorConversion(didList);
      // monitorConversion APIのレスポンスサイズが1未満の場合はmonitorConversion API失敗とする
      if (Object.keys(monitorConversionList).length < 1) {
        return false;
      }
      this.conversionList = monitorConversionList;
      return true;
    },

    /**
     * 変換情報を取得して保持する
     * @param {array} measureDetails 計測データ詳細
     * @returns {boolean} 処理の成否
     */
    setConversionListForPlay(measureDetails) {
      this.referenceValues = measureDetails?.reference_values;
      this.didSizes = measureDetails?.did_sizes;
      const monitorConversionList = this.createConversionMap(measureDetails?.conversion_list);
      // monitorConversion APIのレスポンスサイズが1未満の場合は失敗とする
      if (Object.keys(monitorConversionList).length < 1) {
        return false;
      }
      this.conversionList = monitorConversionList;
      return true;
    },

    /**
     * データ再生を開始する
     * @param {object} measuredData 再生する項目
     * @param {string} specifiedSystemId 再生するECUのID
     * @param {string} projectId プロジェクトID
     * @param {function} callback コールバック関数
     */
    async startPlayApi(measuredData, specifiedSystemId, projectId, callback = () => { }) {
      if (this.measurementUuid === measuredData.session_id) {
        // 言語変更時にデータの再取得を実施しない
        return;
      }
      // データ保存先。計測情報になければ古いデータのためintdash。
      const storage = measuredData.storage || DATA_STORAGE.INTDASH;
      this.measurementUuid = measuredData.session_id;
      this.specifiedSystemId = specifiedSystemId;

      try {
        // 処理中フラグONにし、Intdashとの接続完了までローディングアイコンを出す
        this.processing = true;
        this.isPolling = true;

        const response = await executeSyncRequest(
          this,
          // GET API 呼び出しのコールバック
          async () => {
            return await apiService.getParamCall(
              apiService.OBD,
              `/obd/${measuredData.obdDataId}/measures/${measuredData.measure_id}/dataMonitor`,
              { region_info: this.cachedProjectDataSource },
              this.operationScreen
            );
          },
          // エラー発生のコールバック
          (errorType) => {
            logWrapper.log(
              "ObdDataMonitor.startPlayApi: executeSyncRequest() error: ",
              JSON.stringify(errorType)
            );
            this.showCommonError(ERR_MSG_KEY.MEASURE_START, true);
          },
          // GET API レスポンスチェックのコールバック：デフォルト
          undefined,
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => this.validateErrorResult(result)
        );

        if (!response) return;

        const measureDetails = response.data;

        this.selectedFuncs = (measureDetails?.monitor_items || []).map(monitorItem => {
          const i18nDmItemKey = `pid.${convertKeyForPidToDid(monitorItem.id)}`;
          return {
            data_monitor_id: monitorItem.id,
            menu_title: util.trimStr(this.$t(i18nDmItemKey))
          };
        });

        // 再生 FLG を開始に変更
        // 再生 FLG を変更すると、selectedFuncs を利用したイベントが走るため、selectedFuncs セット後に実施
        this.togglePlaying(true);

        // データ保存先がintdashの場合のみ接続情報取得
        // OBDはindexedDBを使用しないため、S3からの取得時は何もしない
        if (storage === DATA_STORAGE.INTDASH) {
          const isSucceedIntdash = await this.setIntdashInfo();
          // intdashAccess情報取得失敗の場合は以降の処理は行わない。
          if (!isSucceedIntdash) {
            // ボタンの活性・非活性変更
            this.togglePlaying(false);
            return;
          }
        }

        const isSucceedConversionList = await this.setConversionListForPlay(measureDetails);
        // monitorConversion API失敗の場合は以降の処理は行わない。
        if (!isSucceedConversionList) {
          // ボタンの活性・非活性変更
          this.togglePlaying(false);
          return;
        }

        this.hasStopOrder = false;
        // 再生データ取得中はローディングを消す
        this.processing = false;

        this.startRestfulPolling(measuredData.session_id, measuredData.registered_at, projectId, storage);

        // 再生データの取得の終了待ち
        while (this.isPolling && !this.hasStopOrder) {
          await util.sleep(INTERVAL.WAIT_TIME);
        }

      } finally {
        // 処理中フラグOFF
        this.processing = false;
        this.togglePlaying(false);
        // マーカーを反映完了させるため、描画が完全に完了するまで待つ
        await util.sleep(this.getDrawInterval());
        callback();
      }
    },

    /**
     * ダウンストリームの開始処理
     */
    async startDownstream() {
      const conversionKeys = Object.keys(this.conversionList);
      const conversionValues = Object.values(this.conversionList);
      const startMessage = {
        type: WORKER_TASK.START,
        api_token: this.apiToken,
        edge_id: this.edgeId,
        pj_uuid: INTDASH_PROJECT_UUID,
        address: this.endpoint,
        conversionKeys: JSON.parse(JSON.stringify(conversionKeys)),
        conversionValues: JSON.parse(JSON.stringify(conversionValues)),
        didConversionMap: JSON.parse(JSON.stringify(this.didConversionMap)),
        referenceValues: JSON.parse(JSON.stringify(this.referenceValues)),
        didSizes: JSON.parse(JSON.stringify(this.didSizes))
      };
      this.startWorker(WORKER.REALTIME, startMessage, this.iscpDownstreamWorkerListener);
    },

    /**
     * REST APIによるポーリングの開始処理
     * @param {string} measurementId 計測ID
     * @param {string} measuredId 計測登録日時
     * @param {string} projectId プロジェクトID
     * @param {string} storage データ保存先
     */
    async startRestfulPolling(measurementId, measuredId, projectId, storage) {
      const conversionKeys = Object.keys(this.conversionList);
      const conversionValues = Object.values(this.conversionList);
      const messageBase = {
        type: WORKER_TASK.START,
        measurement_id: measurementId,
        conversionKeys: JSON.parse(JSON.stringify(conversionKeys)),
        conversionValues: JSON.parse(JSON.stringify(conversionValues)),
        didConversionMap: JSON.parse(JSON.stringify(this.didConversionMap)),
        referenceValues: JSON.parse(JSON.stringify(this.referenceValues)),
        didSizes: JSON.parse(JSON.stringify(this.didSizes))
      };

      if (storage === DATA_STORAGE.S3) {
        const token = session.getToken();
        const loginToken = session.getLoginToken();
        const startMessage = {
          ...messageBase,
          api_token: token.id_token,
          session_token: loginToken.session_token,
          project_id: projectId,
          measuredId: measuredId,
          cachedProjectDataSource: this.cachedProjectDataSource,
        };
        this.startWorker(WORKER.SSM5_RESTFUL, startMessage, this.restfulWorkerListener);
      } else {
        const startMessage = {
          ...messageBase,
          api_token: this.apiToken,
          pj_uuid: INTDASH_PROJECT_UUID,
          address: this.endpoint,
        };
        this.startWorker(WORKER.RESTFUL, startMessage, this.restfulWorkerListener);
      }
    },

    /**
     * Web workerの開始処理
     * @param {string} workerType 開始するWebWorkerの種別
     * @param {object} startMessage Workerに開始を通知するメッセージ
     * @param {function} listenerCallback Workerからのメッセージを受け取る関数
     */
    async startWorker(workerType, startMessage, listenerCallback) {
      // Workerを作成
      if (!(this.workerInstance instanceof Worker)) {
        // webpackChunkNameを設定した"obd-data-monitor-worker"が、ビルド時のファイル名に使用される
        this.workerInstance = new Worker(
          /* webpackChunkName: "obd-data-monitor-worker" */ new URL('./DataMonitorWorker.js', import.meta.url)
        );
      }

      // Workerに処理開始メッセージを送信
      this.workerInstance.postMessage({ ...startMessage, worker: workerType });

      // Workerエラーフラグのリセット
      this.workerError = false;

      // Workerからメッセージを受信したときの処理
      this.workerInstance.onmessage = (event) => {
        listenerCallback(event);
      };

      // 停止ボタン等で計測停止が指示されるまで待機する
      while (!this.hasStopOrder) {
        // Workerでエラーがあった場合は待機を終了
        if (this.workerError) {
          break;
        }

        // 一定時間スリープ
        await util.sleep(INTERVAL.WORKER_SLEEP);
      }

      if (this.workerInstance instanceof Worker) {
        // ワーカーに停止メッセージ送信
        this.workerInstance.postMessage({ type: WORKER_TASK.STOP, worker: workerType });
      } else {
        console.warn('workerInstance not exist');
      }

      // ポーリング停止
      this.isPolling = false;
    },

    /**
     * 計測データ取得処理の停止
     */
    async stopFetchingMeasurement() {
      this.hasStopOrder = true;
      this.isPolling = false;
      // スリープ分待ち、Workerの停止と直前の実施していた処理を終了させる
      await util.sleep(INTERVAL.WAIT_TIME);
      // 再表示のためにクリアする
      this.measurementUuid = '';
      this.clearMeasuredData();
    },

    /**
     * iscpDownstreamWorkerからのメッセージ受信処理
     * @param {object} event
     */
    iscpDownstreamWorkerListener(event) {
      const data = event.data;

      // エラー受信の場合
      if (data.type === WORKER_EVENT.START_ERROR || data.type === WORKER_EVENT.RUNTIME_ERROR) {
        // Workerでエラーあり
        this.workerError = true;

        // エラーダイアログ表示、データモニター停止
        if (data.type === WORKER_EVENT.START_ERROR) {
          // 計測開始前のため'errorTerminate'として停止させる
          this.stopMonitorErrorTerminate();
          this.showCommonError(ERR_MSG_KEY.FETCH, true);
        } else {
          this.stopMonitor();
          this.showCommonError(ERR_MSG_KEY.FETCH, false);
        }

      }
      // 正常受信(基準時刻)の場合
      else if (data.type === WORKER_EVENT.BASE_TIME) {
        // 基準時刻を設定
        this.baseTime = Math.floor(data.baseTime);
        this.baseTimeOffset = Math.floor(data.offset);
        // Intdashとの接続完了フラグ更新、ローディングを消す。baseTimeがないと後続処理で不都合があるため、基準時刻の受信をもって接続完了とする
        this.isConnected = true;
        // 計測時間確認用のタイマーを、現在時刻で開始
        this.checkMeasuredTime(new Date());
      }
      // 正常受信(Chunkデータ)の場合
      else if (data.type === WORKER_EVENT.CHUNK_DATA) {
        // 変換＆画面描画
        this.dataMonitorList = data.chunkData;
        this.funcValues = data.funcValues;
        this.notifyDataTotalCount(data.originTotalCount);
      }
      // ワーカー停止の場合
      else if (data.type === WORKER_EVENT.STOPPED) {
        // Workerの停止処理が完了したら、Worker自体を終了する
        this.workerInstance.terminate();
        this.workerInstance = {};
        // ボタンの活性・非活性変更
        this.$emit("toggle-execute", false);
      }
    },

    /**
     * RestfulWorkerからのメッセージ受信処理
     * @param {object} event Workerから通知されるイベント
     */
    restfulWorkerListener(event) {
      const data = event.data;

      // エラー受信の場合
      if ([WORKER_EVENT.START_ERROR, WORKER_EVENT.RUNTIME_ERROR, WORKER_EVENT.MEASUREMENT_NOT_FOUND].includes(data.type)) {
        this.isPolling = false;
        this.processing = false;
        // Workerでエラーあり
        this.workerError = true;

        // エラーダイアログ表示
        if (data.type === WORKER_EVENT.START_ERROR) {
          // 再生データ取得前にエラーのため、データを初期化
          this.stopFetchingMeasurement();
          this.showCommonError(ERR_MSG_KEY.FETCH, true);
        } else if (data.type === WORKER_EVENT.MEASUREMENT_NOT_FOUND) {
          // 再生データ取得前にエラーのため、データを初期化。404エラーはinfoダイアログを表示。
          this.stopFetchingMeasurement();
          alert.showInfo(this, null, this.$t("individual.error_data_monitor_data_not_exist"), () => this.$emit("minimum"));
          return;
        } else {
          this.showCommonError(ERR_MSG_KEY.FETCH, false);
        }
      }
      else if (data.type === WORKER_EVENT.MEASUREMENT_INFO) {
        // 基準時刻を設定
        this.baseTime = data.measurement.basetime;
        this.baseTimeOffset = data.measurement.offset;

        // マーカー設定。タグのsharedがtrueであるか、userIdが自分の場合は表示可能なマーカー
        this.markerInfo = data.measurement.markers.filter(marker => {
          const shared = marker.tag?.shared;
          const userId = marker.tag?.userId;
          if (typeof shared === 'string') {
            return shared.toLowerCase() === 'true' || userId === this.userProperty.userId;
          }
          return shared || userId === this.userProperty.userId;
        });
        // intdashからの取得の場合総データポイント数。S3からの取得の場合は総ファイル数。
        this.receivedDataPoints = data.measurement.received_data_points;
      }
      else if (data.type === WORKER_EVENT.CHUNK_DATA) {
        if (data.chunkData.length === 0 || (!this.inExecution && !this.inPlaying)) {
          this.hasStopOrder = true;
        }
        // 変換＆画面描画
        this.dataMonitorList = data.chunkData;
        this.funcValues = data.funcValues;
        this.escapedDataPoints = data.escapedDataPoints;
        this.$emit("update-total-count", data.originTotalCount);
      }
      else if (data.type === WORKER_EVENT.SUCCESS_END) {
        this.hasStopOrder = true;
        // ワーカーの処理正常終了時、全データ取得完了として総データポイント数を通知する
        this.$emit("update-total-count", this.receivedDataPoints);
      }
      // ワーカー停止の場合
      else if (data.type === WORKER_EVENT.STOPPED) {
        // Workerの停止処理が完了したら、Worker自体を終了する
        this.workerInstance.terminate();
        this.workerInstance = {};
      }
    },

    /**
     * 計測時間確認処理
     * 計測APIが最大計測時間を超えたら停止
     * @param {*} startTime タイマーの開始日時
     */
    checkMeasuredTime(startTime) {
      const startDate = !startTime ? new Date() : startTime;
      this.timerId = setInterval(() => {
        if (this.hasStopOrder) {
          clearInterval(this.timerId);
          return;
        }

        const endTime = new Date();
        const measurementTime = endTime.getTime() - startDate.getTime();
        if (measurementTime >= MAX_TIME) {
          clearInterval(this.timerId);
          this.isPolling = false;
          // 計測API停止
          this.stopMonitor();
          alert.showWarning(
            this,
            null,
            this.$t("individual.alert_data_monitor_data_limit_exceeded"),
            () => { }
          );
        }
      }, INTERVAL.WATCH_TIMER);
    },

    /**
     * その他のエラー
     * @param {string} action エラーメッセージの種別
     * @param {boolean} isBack 信号線選択画面に戻る場合はtrueを指定
     */
    showCommonError(action, isBack) {
      let messageKey;
      switch (action) {
        // 操作時のエラー
        case ERR_MSG_KEY.INIT:
          messageKey = "fixed_resource.obd_function_common_message_result_ng_2";
          break;
        case ERR_MSG_KEY.MEASURE_START:
          messageKey = "individual.error_data_monitor_measure_start";
          break;
        case ERR_MSG_KEY.MEASURE_STOP:
          messageKey = "individual.error_data_monitor_measure_stop";
          break;
        case ERR_MSG_KEY.INFO_GET:
          messageKey = "individual.error_data_monitor_info_get";
          break;
        case ERR_MSG_KEY.MEASUREMENT_LIMIT:
          messageKey = "individual.error_data_monitor_measurement_count_limit";
          break;
        // 以下はIntadashサーバーの接続断時のエラー
        case ERR_MSG_KEY.INTDASH:
          messageKey = "individual.error_data_monitor_intdash";
          break;
        case ERR_MSG_KEY.REQUESTS_START:
          messageKey = "individual.error_data_monitor_requests_start";
          break;
        case ERR_MSG_KEY.FETCH:
          messageKey = "individual.error_data_monitor_fetch";
          break;
        case ERR_MSG_KEY.REQUESTS_EXECUTION:
          messageKey = "individual.error_data_monitor_requests_execution";
          break;

        // 一般的な通信エラー
        case ERR_MSG_KEY.COMMUNICATION:
        default:
          messageKey = "individual.error_communication_error";
          break;
      }

      if (!this.isError) {
        this.isError = true;
        alert.showError(this, null, this.$t(messageKey), () => { isBack ? this.back() : this.editFlg(); });
      }
    },

    /**
     * 計測を停止して項目選択画面に戻る
     */
    back() {
      this.isError = false;
      this.$emit("toggle-execute", false);
      this.$emit("toggle-show-select", true);
    },

    /**
     * エラーフラグをOFFにする
     */
    editFlg() {
      this.isError = false;
    },

    /**************** 計測API処理 ***************/

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      if (this.signout) {
        return;
      }

      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック
        (self) => {
          self;
          this.$emit("toggle-execute", false);
          this.logout(this);
        },
        // セッションリセット処理のコールバック
        (self) => {
          self;
          this.$emit("toggle-execute", false);
          this.resetSession(this);
        },
        // その他エラー発生のコールバック
        (result) => {
          result;
          this.showCommonError(ERR_MSG_KEY.COMMUNICATION, true);
        }
      );
    },

    /**
     * intdashアクセス情報取得処理
     */
    async getIntdashAccess() {
      let queryParam = null;
      if (this.specifiedSystemId) {
        queryParam = { type: "playback", region_info: this.cachedProjectDataSource };
      } else {
        queryParam = { type: "data_monitor", net_app_id: this.netAppId };
      }

      try {
        const response = await executeSyncRequest(
          this,
          // GET API 呼び出しのコールバック
          async () => {
            return await apiService.getParamCall(
              apiService.OBD,
              '/intdashAccessInfo',
              queryParam,
              this.operationScreen,
            );
          },
          // エラー発生のコールバック
          (errorType) => {
            logWrapper.log('getIntdashAccess() syncApi() error: ', JSON.stringify(errorType));
            if (errorType === ERR_MSG_KEY.INTDASH) {
              this.showCommonError(ERR_MSG_KEY.INTDASH, true);
            } else {
              this.showCommonError(ERR_MSG_KEY.MEASURE_START, true);
            }
          },
          // GET API レスポンスチェックのコールバック
          (getApiResponse) => {
            // intdashアクセス情報正常が入っていれば成功とする。ただし再生画面ではedgeIdは返されない
            if (getApiResponse &&
              getApiResponse.data &&
              getApiResponse.data.api_token &&
              (this.specifiedSystemId || getApiResponse.data.edge_id) &&
              getApiResponse.data.expires_in &&
              getApiResponse.data.endpoint) {
              return "";
            } else if (getApiResponse.status !== 200 && getApiResponse.data.code === error.DMAT_BACKEND_ACCESS_TO_INTDASH_FAILED) {
              return ERR_MSG_KEY.INTDASH;
            } else {
              return POLLING_ERROR_GET_INVALID_RESPONSE;
            }
          },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => {
            this.validateErrorResult(result);
          }
        );
        if (response &&
          response.data &&
          response.data.api_token &&
          (this.specifiedSystemId || response.data.edge_id) &&
          response.data.expires_in &&
          response.data.endpoint) {
          // intdashアクセス情報を格納
          this.apiToken = response.data.api_token;
          this.edgeId = response.data.edge_id;
          this.tkExpire = response.data.expires_in;
          this.endpoint = response.data.endpoint;
        }
      } catch (e) {
        this.showCommonError(ERR_MSG_KEY.MEASURE_START, true);
      }
    },

    /**
     * データモニタ変換情報取得処理
     * @param {array} targetDidListArr 選択項目List
     * @param {string} projectId プロジェクトのID（再生画面でのみ使用）
     * @param {number} registeredAt 計測登録日時のepoch秒（再生画面でのみ使用）
     * @returns {array} conversionInfo データモニタ変換情報
     */
    async getMonitorConversion(targetDidListArr) {
      try {
        let queryParam = {};
        if (this.specifiedSystemId) {
          queryParam = { region_info: this.cachedProjectDataSource };
        }

        const response = await executePolling(
          this,
          // POST API 呼び出しのコールバック
          async () => {
            // API取得開始 FLG 変更
            return await apiService.postCall(
              apiService.OBD,
              `/netApps/${this.netAppId}/obd/${this.obdDataId}/systems/${this.systemNo}/dataMonitor`,
              {
                target_pid_did_list: targetDidListArr,
                type: "setMonitorConversion",
              },
              this.operationScreen,
            );
          },
          // GET API 呼び出しのコールバック
          async (postApiResponse) => {
            return await apiService.getParamCall(
              apiService.OBD,
              `/netApps/${this.netAppId}/requests/${postApiResponse.data.request_id}`,
              queryParam,
              this.operationScreen,
            );
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showCommonError(ERR_MSG_KEY.MEASURE_STOP, false); },
          // POST API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          // GET API レスポンスチェックのコールバックはデフォルト処理を使用
          undefined,
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );

        // 演算値の上書きに使用する値を格納
        if (response && response.data && response.data.results.read_results) {
          this.referenceValues = response.data.results.read_results;
        }

        // DID/PIDごとのデータサイズを格納
        if (response && response.data && response.data.results.did_sizes) {
          this.didSizes = response.data.results.did_sizes;
        }

        // 変換情報の Map
        return this.createConversionMap(response?.data?.results?.conversion_list);
      } catch (e) {
        logWrapper.log(e.messeage, e.cause || '');
        this.showCommonError(ERR_MSG_KEY.COMMUNICATION, true);
        return null;
      }
    },

    /**
     * データモニタ変換情報のマップを作成する
     * @param {Array<object>} conversion_list Backend から取得した変換情報
     * @returns {Map<string, object>} Frontend 用に変換した Map
     */
    createConversionMap(conversion_list) {
      // 返却する配列
      const returnVal = {};
      (conversion_list || []).forEach(item => {
        returnVal[item.pid_did] = item.info;
      });

      this.didConversionMap = {};
      // 変換処理で変換情報を早く引くためのオブジェクトを別途作成。1つのDIDに複数の信号線があるため、DIDをキーに配列で保持
      Object.keys(returnVal).forEach((itemId, index) => {
        const did = itemId.match(/^[^_]+/)[0];
        if (this.didConversionMap[did]) {
          this.didConversionMap[did].push(index);
        } else {
          this.didConversionMap[did] = [index];
        }
      });

      // APIのresponseをまとめた返却値を返却
      return returnVal;
    },

    /**
     * 計測開始API Polling処理
     * @param {string} token id_token
     * @param {array} selected_did_list 選択項目List
     */
    async measureStart(token, selected_did_list) {
      this.hasStopOrder = false;
      // Polling loop Count
      let loopCnt = 0;
      // ネットワークエラーが発生した回数
      let networkErrorCount = 0;
      try {
        // polling.jsはセッションタイマー停止機能がないため、計測時に事前に停止させる
        this.setIsTimeoutLocked(true);
        this.stopSessionTimer();
        let queryParam = {};
        if (this.specifiedSystemId) {
          queryParam = { region_info: this.cachedProjectDataSource };
        }
        const response = await executePolling(
          this,
          // POST API 呼び出しのコールバック
          async () => {
            // API取得開始 FLG 変更
            return await apiService.postCall(
              apiService.OBD,
              `/netApps/${this.netAppId}/obd/${this.obdDataId}/systems/${this.systemNo}/dataMonitor`,
              {
                id_token: token.id_token,
                edge_id: this.edgeId,
                measure_uuid: this.measurementUuid,
                monitor_item_list: selected_did_list,
                type: "startDataMonitor",
                is_persist: this.dataMonitorFunctionSettings.isPersist
              },
              this.operationScreen
            );
          },
          // GET API 呼び出しのコールバック
          async (postApiResponse) => {
            // postApi取得結果が正常の場合
            if (this.isCompleted) {
              // sleepを入れてgetの周期を遅らせる
              await util.sleep(INTERVAL.MEASURE_SLEEP);
            }
            const getApiResponse = await apiService.getParamCall(
              apiService.OBD,
              `/netApps/${this.netAppId}/requests/${postApiResponse.data.request_id}`,
              queryParam,
              this.operationScreen,
            );
            const isNetworkError = (
              getApiResponse.data.code === error.FRONT_DISCONNECT_NETWORK ||
              getApiResponse.data.code === error.FRONT_SERVER_NO_RESPONCE
            );
            if (isNetworkError && (networkErrorCount + 1) <= RETRY.MEASURE_POLLING_NETWORK_ERROR) {
              // ネットワーク切断時のエラーは、1分間は正常なレスポンスに置き換えてポーリングを続行させる
              networkErrorCount++;
              getApiResponse.data.code = null;
              getApiResponse.data.status = this.isCompleted ? REQUEST_STATUS_COMPLETED : REQUEST_STATUS_IN_PROGRESS;
              getApiResponse.status = 200;
            } else {
              networkErrorCount = 0;
            }
            return getApiResponse;
          },
          // エラー発生のコールバック
          (errorType) => {
            logWrapper.log('measureStart() pollingApi() error: ', JSON.stringify(errorType));
            this.isPolling = false;
            this.checkProcessing = false;
            // 計測停止 FLG変更
            this.hasStopOrder = true;
            // 開始前
            if (!this.isCompleted) {
              if (errorType === error.TOO_MANY_REQUESTS_EXCEPTION) {
                this.showCommonError(ERR_MSG_KEY.MEASUREMENT_LIMIT, true);
              } else {
                this.showCommonError(ERR_MSG_KEY.MEASURE_START, true);
              }
            }
            // 開始後
            else {
              this.showCommonError(ERR_MSG_KEY.REQUESTS_EXECUTION, false);
            }
          },
          // POST API レスポンスチェックのコールバック。デフォルト処理の前に件数エラーの確認をする
          (postApiResponse) => {
            if (!postApiResponse || !postApiResponse.data) {
              return POLLING_ERROR_POST_INVALID_RESPONSE;
            }
            if (postApiResponse.data.async_code === error.TOO_MANY_REQUESTS_EXCEPTION) {
              // 既に3200台が計測中の場合のエラー
              return error.TOO_MANY_REQUESTS_EXCEPTION;
            }
            return postResponseCheckDefaultAction(postApiResponse);
          },
          // GET API レスポンスチェックのコールバック
          (getApiResponse) => {
            if (this.signout) {
              // サインアウト後は後続処理を行ってもエラーとなるため処理終了
              return REQUEST_STATUS_COMPLETED;
            }
            this.checkProcessing = true;
            let result = POLLING_ERROR_GET_INVALID_RESPONSE;
            result; // to avoid warning
            if (
              getApiResponse &&
              getApiResponse.data &&
              getApiResponse.data.status
            ) {
              switch (getApiResponse.data.status) {
                case REQUEST_STATUS_COMPLETED: {

                  // 計測中のCC->Intdash送信エラー発生の場合
                  if (util.parseBoolean(getApiResponse.data.int_dash_sending_error)) {
                    this.showCommonError(ERR_MSG_KEY.REQUESTS_EXECUTION, false);
                    break;
                  }

                  // 正常時
                  // Completed 初回処理
                  if (!this.isCompleted) {
                    // ボタンの活性・非活性変更
                    this.$emit("toggle-execute", true);
                    // Completed FLG変更
                    this.isCompleted = true;
                  }

                  // リトライ回数判定
                  loopCnt++;
                  // API取得回数 >= リトライ回数の場合
                  if (
                    loopCnt >= RETRY.MEASURE_START ||
                    !this.isPolling ||
                    this.hasStopOrder
                  ) {
                    // Pollingを正常終了
                    result = REQUEST_STATUS_COMPLETED;
                    // Polling FLG変更
                    this.isPolling = false;
                    // checkGetApiリトライ中 FLG変更
                    this.checkProcessing = false;
                    // 計測停止 FLG変更
                    this.hasStopOrder = true;
                    // 上記以外
                  } else {
                    // Pollingを続ける
                    result = REQUEST_STATUS_IN_PROGRESS;
                    loopCnt = 0;
                  }
                  break;
                }
                case REQUEST_STATUS_IN_PROGRESS: {
                  if (!this.isCompleted) {
                    result = REQUEST_STATUS_IN_PROGRESS;

                    // リトライ回数判定
                    loopCnt++;

                    if (loopCnt >= RETRY.MEASURE_START) {
                      this.isPolling = false;
                      this.checkProcessing = false;
                    }
                  }
                  break;
                }
                case REQUEST_STATUS_FAILED: {
                  this.isPolling = false;
                  this.checkProcessing = false;
                  if (getApiResponse.data.async_code === error.DMAT_CLOUD_CONNECTOR_ACCESS_TO_INTDASH_FAILED) {
                    this.stopMonitorErrorTerminate();
                    this.showCommonError(ERR_MSG_KEY.REQUESTS_START, true);
                  } else {
                    if (this.isCompleted) {
                      // CCからcompleteが1度でも帰った場合のみ、停止を呼ぶ
                      this.stopMonitor();
                    }
                    this.showCommonError(ERR_MSG_KEY.REQUESTS_EXECUTION, false);
                  }
                  break;
                }
                default:
                  this.isPolling = false;
                  this.checkProcessing = false;
                  break;
              }
            }
            return result;
          },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => {
            this.isPolling = false;
            this.checkProcessing = false;
            this.validateErrorResult(result);
          },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          () => {
            if (this.signout) {
              // サインアウト後は後続処理を行ってもエラーとなるため処理終了
              return;
            }
            this.isPolling = false;
            this.checkProcessing = false;
            // 開始処理自体は行われた後のため、取得でのエラー時は停止APIを呼ぶ。
            this.stopMonitor();
            this.showCommonError(ERR_MSG_KEY.FETCH, false);
          },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック関数
          undefined,
          // ポーリングのリトライ回数指定
          RETRY.MEASURE_START
        );
        return response;
      } finally {
        // タイムアウト停止の終了
        this.resetSessionTimer();
        this.setIsTimeoutLocked(false);
      }
    },

    /**
     * データモニタ停止処理
     * @param {Array<object>} selected_did_list 選択項目リスト
     * @param {boolean} errorTerminateFlg errorTerminateFlgフラグ(trueの場合typeをerrorTerminateにする)
     */
    async measureStop(selected_did_list, errorTerminateFlg) {
      this.memoUuid = "";
      this.$emit("toggle-execute", false);
      this.hasStopOrder = true;

      const monitorItemList = selected_did_list.map(did => ({ id: did }));
      let queryParam = {};
      if (this.specifiedSystemId) {
        queryParam = { region_info: this.cachedProjectDataSource };
      }
      // 停止処理と確認のポーリング処理をひとまとめに関数化
      const runStopAndCheck = async () => await executePolling(
        this,
        // POST API 呼び出しのコールバック
        async () => {
          return await apiService.postCall(
            apiService.OBD,
            `/netApps/${this.netAppId}/obd/${this.obdDataId}/systems/${this.systemNo}/dataMonitor`,
            {
              type: errorTerminateFlg ? "error_terminate" : "stopDataMonitor",
              measure_uuid: this.measurementUuid,
              is_persist: this.dataMonitorFunctionSettings.isPersist,
              monitor_item_list: monitorItemList,
              reference_values: this.referenceValues || [],
            },
            this.operationScreen
          );
        },
        // GET API 呼び出しのコールバック
        async (postApiResponse) => {
          return await apiService.getParamCall(
            apiService.OBD,
            `/netApps/${this.netAppId}/requests/${postApiResponse.data.request_id}`,
            queryParam,
            this.operationScreen,
          );
        },
        // エラー発生のコールバック
        (errorType) => {
          logWrapper.log('measureStop() pollingApi() error: ', JSON.stringify(errorType));
          this.showCommonError(ERR_MSG_KEY.MEASURE_STOP, false);
        },
        // POST API レスポンスチェックのコールバックはデフォルト処理を使用
        undefined,
        // GET API レスポンスチェックのコールバック
        (getApiResponse) => {
          let result = POLLING_ERROR_GET_INVALID_RESPONSE;
          if (
            getApiResponse &&
            getApiResponse.data &&
            getApiResponse.data.status
          ) {
            switch (getApiResponse.data.status) {
              case REQUEST_STATUS_COMPLETED:
                result = REQUEST_STATUS_COMPLETED;
                this.$emit("toggle-execute", false);
                break;
              case REQUEST_STATUS_IN_PROGRESS:
                result = REQUEST_STATUS_IN_PROGRESS;
                break;
              case REQUEST_STATUS_FAILED:
              default:
                this.$emit("toggle-execute", false);
                break;
            }
          }
          return result;
        },
        // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          // 停止APIから異常なAPIレスポンス（ネットワークエラーなど）の場合はエラー状態を保持
          this.stopError = result;
        },
        // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
        (result) => {
          // 停止確認APIから異常なAPIレスポンス（ネットワークエラーなど）の場合はエラー状態を保持
          this.stopError = result;
        },
        RETRY.MEASURE_STOP
      );

      let stopRetryCount = 0;
      let response = null;

      try {
        // 計測停止中はセッションタイマーを停止
        this.setIsTimeoutLocked(true);
        this.stopSessionTimer();

        while (stopRetryCount <= RETRY.MEASURE_STOP_NETWORK_ERROR) {
          this.stopError = null;
          // 停止処理と確認のポーリング処理を実行
          response = await runStopAndCheck();
          if (!this.stopError) {
            if (response) {
              this.memoUuid = response.data.results?.measure_id || "";
            }
            break;
          }
          if (RETRY.MEASURE_STOP_NETWORK_ERROR <= stopRetryCount) {
            // リトライしてもエラーであれば、ダイアログを表示してログイン画面へ
            this.validateErrorResult(this.stopError);
            break;
          }
          stopRetryCount++;
          await util.sleep(INTERVAL.NETWORK_ERROR);
        }
      } finally {
        // タイムアウト停止の終了
        this.resetSessionTimer();
        this.setIsTimeoutLocked(false);
      }
    },

    setSignout() {
      this.signout = true;
    },

    // グラフ設定
    handleConfigureGraph() {
      this.$refs.data_monitor_execute.handleConfigureGraph();
    },

    /**
     * ズームリセット
     */
    handleResetZoom() {
      this.$refs.data_monitor_execute.handleResetZoom();
    },

    /** データモニタの停止 */
    execStopMonitor() {
      this.$emit('exec-stop-monitor');
    },

    /** 項目選択に戻る */
    backSelect() {
      this.unmountMeasure = true;
      // 描画してMeasureコンポーネントを確実に消してから戻る
      this.$nextTick(() => {
        this.$emit('back-select');
        this.unmountMeasure = false;
      });
    },

    /** 信号を選択していない場合、メモ、グラフ設定ボタンを消す */
    changeNoSignal(value) {
      this.$emit('change-no-signal', value);
    },

    /** モニタ停止(errorTerminateで停止) */
    async stopMonitorErrorTerminate() {
      await this.stopMonitor(true);
    },

    /**
     * 計測IDを取得する
     */
    getMeasurementId() {
      return this.measurementUuid;
    },
  },

  created() {
    // noop
  },

  mounted() {
    this.mountedLogic();
  },

  beforeMount() {
    // 計測データを初期化。変更が多いためdataに持たせたくなく、warningとなるためbeforeMountで初期化する
    this.dataMonitorList = [];
  },

  beforeUnmount() {
    // Worker停止に失敗していた場合、画面を離れる前にWorkerを終了する
    if (this.workerInstance instanceof Worker) {
      this.workerInstance.terminate();
      this.workerInstance = {};
    }
  }
};
